import * as yup from 'yup';
import dayjs from 'dayjs';
import { CalculusTFunction } from '@retail/calculus/i18n';
import {
  dateRangeSchema,
  getConditionBindingValuesFromLevelSchema,
  levelSelectSchema,
  roundingIdSchema,
  salesPriceConditionSchema,
} from '@retail/calculus/components';
import { freightConditionsSchema } from '@retail/freight/components';
import { getISO_8601Date } from '@retail/utils';
import {
  ConditionBinding,
  ConditionsNew,
  CreateCalculusConditions,
  PriceType,
} from '@retail/calculus/types';
import { PackageType, PriceSuggestionType } from '@retail/products/types';

export type ConditionStepType = 'purchase' | 'freight' | 'salesPrice' | 'rounding';

export const allConditionsSchema = (t: CalculusTFunction) =>
  levelSelectSchema(t)
    .concat(dateRangeSchema(t))
    .concat(freightConditionsSchema(t))
    .concat(salesPriceConditionSchema(t))
    .concat(roundingIdSchema(t))
    .concat(
      yup.object({
        agreementId: yup.string().default(''),
        priceType: yup.string().default(''),
        packageType: yup.string().default(''),
        gtin: yup.string().default(''),
        purchaseSkipped: yup.boolean().default(false),
        freightSkipped: yup.boolean().default(false),
        salesPriceSkipped: yup.boolean().default(false),
        roundingSkipped: yup.boolean().default(false),
      })
    );

export type AllConditionsFormValues = yup.InferType<ReturnType<typeof allConditionsSchema>>;

export const allConditionsDefaultValues = (
  t: CalculusTFunction,
  hasSupplier: boolean
): AllConditionsFormValues =>
  ({
    ...allConditionsSchema(t).getDefault(),
    level: hasSupplier ? '' : 'assortment',
  } as AllConditionsFormValues);

const levelFromBindingType = (row: ConditionsNew): AllConditionsFormValues['level'] => {
  switch (row.bindingType) {
    case 'MG_PARTICIPANT_ACCOUNT':
      return 'supplier';
    case 'OVERGRUPPE':
    case 'HOVEDGRUPPE':
    case 'VAREGRUPPE':
      return 'productGroup';
    case 'DISCOUNT_GROUP':
      return 'discountGroup';
    case 'MG_SUPPLIER_ITEM':
      return 'item';
    default:
      return 'assortment';
  }
};

export const editConditionDefaultValues = (
  t: CalculusTFunction,
  row: ConditionsNew
): AllConditionsFormValues => {
  const values = allConditionsSchema(t).getDefault() as AllConditionsFormValues;
  values.level = levelFromBindingType(row);

  if (values.level === 'productGroup') {
    values.productGroup = { groupNumber: row.bindingTargetId };
  }
  if (values.level === 'discountGroup') {
    values.discountGroups = [{ code: row.bindingTargetId }];
  }
  if (values.level === 'item' && row.bindingTargetItem) {
    values.item = {
      id: row.bindingTargetItem.supplierItemId,
      mgItemNumber: row.bindingTargetItem.mgItemNumber,
    };
  }

  const ownPurchase =
    row.purchaseCondition && !row.purchaseCondition.isInherited ? row.purchaseCondition : undefined;
  if (ownPurchase) {
    values.agreementId = String(ownPurchase.agreementId);
    values.priceType = ownPurchase.priceType ?? '';
    values.packageType = ownPurchase.packageType ?? '';
  }

  const ownFreight =
    row.freightCondition && !row.freightCondition.isInherited ? row.freightCondition : undefined;
  if (ownFreight?.percentAddon != null) {
    values.freightType = 'percent';
    values.percentAddon = ownFreight.percentAddon;
  } else if (ownFreight?.fixedAddOn != null) {
    values.freightType = 'fixed_addon';
    values.fixedAddon = ownFreight.fixedAddOn;
    values.fixedAddonUnit = ownFreight.fixedAddOnUnit ?? '';
  }

  const ownPriceSource =
    row.priceSourceCondition && !row.priceSourceCondition.isInherited
      ? row.priceSourceCondition
      : undefined;
  if (ownPriceSource) {
    values.priceSourcePriority = ownPriceSource.priorities.map(
      (prio) => prio.priceSuggestionType
    ) as PriceSuggestionType[];
  }
  const ownMarkup =
    row.markupCondition && !row.markupCondition.isInherited ? row.markupCondition : undefined;
  if (ownMarkup) {
    values.markupCoverageDegree = ownMarkup.coverageDegree;
  }

  const ownRounding =
    row.roundingCondition && !row.roundingCondition.isInherited ? row.roundingCondition : undefined;
  if (ownRounding) {
    values.priceRoundingId = ownRounding.ruleSetId;
  }

  const validFrom =
    ownPriceSource?.validFrom ??
    ownRounding?.validFrom ??
    ownPurchase?.validFrom ??
    ownFreight?.validFrom ??
    ownMarkup?.validFrom;
  if (validFrom) {
    // An existing condition may have a validFrom in the past, which the date field rejects; those
    // default to today. The area is still only re-sent if its value actually changes (see baseline
    // handling in buildConditionsFromForm), so this alone never creates a change.
    values.fromDate = dayjs(validFrom).isBefore(dayjs(), 'day') ? new Date() : validFrom;
  }

  return values;
};

// True when the edited form value of an area differs from the pre-filled baseline. Used so an
// edit only sends the areas the user actually changed (a date-only bump is not a change).
const areaChanged = (
  values: AllConditionsFormValues,
  baseline: AllConditionsFormValues,
  area: ConditionStepType
): boolean => {
  const eq = (a: unknown, b: unknown) => String(a ?? '') === String(b ?? '');
  switch (area) {
    case 'purchase':
      return (
        !eq(values.agreementId, baseline.agreementId) ||
        !eq(values.priceType, baseline.priceType) ||
        !eq(values.packageType, baseline.packageType)
      );
    case 'freight':
      return (
        !eq(values.percentAddon, baseline.percentAddon) ||
        !eq(values.fixedAddon, baseline.fixedAddon) ||
        !eq(values.fixedAddonUnit, baseline.fixedAddonUnit)
      );
    case 'salesPrice':
      return (
        JSON.stringify(values.priceSourcePriority ?? []) !==
          JSON.stringify(baseline.priceSourcePriority ?? []) ||
        !eq(values.markupCoverageDegree, baseline.markupCoverageDegree)
      );
    case 'rounding':
      return !eq(values.priceRoundingId, baseline.priceRoundingId);
    default:
      return true;
  }
};

export const stepValidationSchema = (t: CalculusTFunction, step: ConditionStepType | 'level') => {
  switch (step) {
    case 'level':
      return levelSelectSchema(t);
    case 'purchase':
      return yup.object({
        agreementId: yup.string().required(t('calculus.error.requiredField')),
      });
    case 'freight':
      return freightConditionsSchema(t);
    case 'salesPrice':
      return salesPriceConditionSchema(t).concat(
        yup.object({
          priceSourcePriority: yup
            .array()
            .min(1, t('calculus.error.requiredField'))
            .required(t('calculus.error.requiredField')),
        })
      );
    case 'rounding':
      return roundingIdSchema(t);
    default:
      return undefined;
  }
};

export const buildConditionsFromForm = (
  values: AllConditionsFormValues,
  supplierNumber?: number,
  bindingOverride?: ConditionBinding[],
  // When editing, the pre-filled baseline: an area is only sent if its value differs from it.
  baseline?: AllConditionsFormValues
): CreateCalculusConditions[] => {
  const bindings =
    bindingOverride ??
    getConditionBindingValuesFromLevelSchema({ levelSchema: values, supplierNumber });
  const validFrom = getISO_8601Date(values.fromDate);
  const conditions: CreateCalculusConditions[] = [];
  const changed = (area: ConditionStepType) => !baseline || areaChanged(values, baseline, area);

  if (!values.purchaseSkipped && values.agreementId && changed('purchase')) {
    const content = {
      purchase: {
        agreementId: Number(values.agreementId),
        priceType: values.priceType ? (values.priceType as PriceType) : undefined,
        packageType: values.packageType ? (values.packageType as PackageType) : undefined,
      },
    };
    bindings.forEach((binding) => conditions.push({ content, binding, validFrom }));
  }

  if (!values.freightSkipped && changed('freight')) {
    const freight = values.percentAddon ? { percentAddon: values.percentAddon } : undefined;
    const freightFixed =
      values.fixedAddon && values.fixedAddonUnit
        ? { fixedAddOn: values.fixedAddon, fixedAddOnUnit: values.fixedAddonUnit }
        : undefined;
    if (freight || freightFixed) {
      const content = { freight, freightFixed };
      bindings.forEach((binding) => conditions.push({ content, binding, validFrom }));
    }
  }

  if (!values.salesPriceSkipped && values.priceSourcePriority?.length && changed('salesPrice')) {
    if (values.markupCoverageDegree != null) {
      const content = { markupCoverageDegree: { coverageDegree: values.markupCoverageDegree } };
      bindings.forEach((binding) => conditions.push({ content, binding, validFrom }));
    }
    const priorities = (
      values.priceSourcePriority.includes('MARKUP')
        ? values.priceSourcePriority
        : [...values.priceSourcePriority, 'MARKUP']
    ) as PriceSuggestionType[];
    bindings.forEach((binding) =>
      conditions.push({ content: { priceSuggestion: { priorities } }, binding, validFrom })
    );
  }

  if (!values.roundingSkipped && values.priceRoundingId && changed('rounding')) {
    const content = { rounding: { roundingRuleSetId: values.priceRoundingId } };
    bindings.forEach((binding) => conditions.push({ content, binding, validFrom }));
  }

  return conditions;
};
